import { getAuth } from 'firebase/auth'
import * as Sentry from '@sentry/browser'
import { z } from 'zod'
import { MatterTable, TableSchema } from '../schemas'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { readResponseJson, getResponseDebugContext } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'

/** GET the table endpoint and validate its response contract. */
export async function getTables(matterId: string, signal?: AbortSignal): Promise<MatterTable[]> {
  const token = await getAuth().currentUser?.getIdToken()
  if (!token) throw new Error('Sign in to access tables.')
  const url = `${kPaxtonAppApiRESTBaseUrl()}/api/v1/matter/${matterId}/tables`
  const response = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    signal,
  })
  const { data, parseError } = await readResponseJson(response)
  // Fetch can resolve its headers before cleanup aborts the body read.
  signal?.throwIfAborted()
  if (!response.ok) {
    const detail = data && typeof data === 'object' && 'detail' in data ? data.detail : undefined
    if (response.status >= 500) Sentry.captureException(new Error('Table request failed'), { extra: getResponseDebugContext(response, url) })
    throw new ApiError(
      response.status,
      response.status >= 400 && response.status < 500 && typeof detail === 'string' ? detail : 'Unable to load tables.',
      response.status < 500 ? detail : undefined
    )
  }
  const parsed = z.array(TableSchema).safeParse(data)
  if (parseError || !parsed.success) {
    Sentry.captureException(new Error('Invalid table response'), {
      extra: {
        ...getResponseDebugContext(response, url),
        responseFailure: parseError ? 'body-read' : 'schema',
        // Parser messages and schema inputs can contain table contents; record only structure.
        parseErrorName: parseError instanceof Error ? parseError.name : null,
        validationIssues: !parseError && !parsed.success ? parsed.error.issues.map(({ code, path }) => `${code} at ${path.join('.')}`) : null,
      },
    })
    throw new ApiResponsePayloadError(response.status, 'The table response could not be read. Please retry.')
  }
  return parsed.data
}
